// The off-chain leaf ledger (docs/01-architecture.md section 10). One record
// per (vault, tree, leaf) from reservation on; records are never deleted. A
// tree marker says this device saw the tree fresh (created it, or rotated
// into it): only such trees may sign ordinary operations. Without a marker
// the signer is restricted to the tree's recovery message (section 9).

export type LeafStatus = 'reserved' | 'signed' | 'sent' | 'landed' | 'burned';
export const UNRESOLVED: readonly LeafStatus[] = ['reserved', 'signed', 'sent'];

export type Purpose = 'swap-exact' | 'swap-bounded' | 'transfer' | 'rotation' | 'recovery' | 'close' | 'other';

export interface LeafRecord {
  /** `${vaultId}:${treeIndex}:${leaf}` */
  readonly key: string;
  readonly vaultId: string;
  readonly treeIndex: number;
  readonly leaf: number;
  /** Hex; fixed at reservation. A leaf never carries a second digest. */
  readonly digest: string;
  readonly purpose: Purpose;
  status: LeafStatus;
  /** Hex, present from `signed` on; persisted before it is ever returned to a caller. */
  signature?: string;
  saltTrial?: number;
  /** Hex of the successor key when the authorization carries NEXT_KEY (rotation or recovery). */
  nextKey?: string;
  /** Decimal u64; `18446744073709551615` for the recovery message. */
  readonly validUntilSlot: string;
  txSignature?: string;
  note?: string;
  op?: StoredOp;
  readonly createdAt: number;
  updatedAt: number;
}

/** An Execute operation a record authorizes, stored at reservation so a crashed device can rebuild the exact instruction (hex fields). */
export interface StoredExecOp {
  header: string;
  aux: { pubkey: string; isSigner: boolean; isWritable: boolean }[];
  inners: { program: string; metas: { pubkey: string; isWritable: boolean }[]; data: string }[];
}

/** A Close operation a `purpose: 'close'` record authorizes: the fields `closeDigest` binds beyond what the record itself already carries (hex/decimal). */
export interface StoredCloseOp {
  destination: string;
  validUntilSlot: string;
}

/** The operation a record authorizes. Unchanged on the wire for Execute (`StoredExecOp`); `StoredCloseOp` is new and distinguished structurally (no `header` field). */
export type StoredOp = StoredExecOp | StoredCloseOp;

export type TreeOrigin = 'created' | 'rotated' | 'recovered';

export interface TreeMarker {
  /** `${vaultId}:${treeIndex}` */
  readonly key: string;
  readonly vaultId: string;
  readonly treeIndex: number;
  readonly origin: TreeOrigin;
  /**
   * Decimal u64. The marker is fresh only while the chain's current slot is
   * at or below this value; past it the device is recovery-only until it
   * rotates (R20). Written `currentSlot + MARKER_LIFETIME_SLOTS` at creation
   * and renewed the same way on every `sync` while the tree stays normal, so
   * a device that reopens long after its last sync must rotate first.
   */
  readonly validUntilSlot: string;
  readonly createdAt: number;
}

export const recordKey = (vaultId: string, treeIndex: number, leaf: number): string => `${vaultId}:${treeIndex}:${leaf}`;
export const markerKey = (vaultId: string, treeIndex: number): string => `${vaultId}:${treeIndex}`;

/** The resolved statuses (docs/17 L8): no store method gives a `landed` or `burned` row an unresolved status again. */
export const TERMINAL: readonly LeafStatus[] = ['landed', 'burned'];

/**
 * Durable storage for the ledger. Every `put` must be committed before it
 * resolves: the signer returns a signature to its caller only after the
 * `signed` record is stored (persist-before-exposure).
 */
export class LedgerConflict extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LedgerConflict';
  }
}

/**
 * One record write (docs/17 L8): maps the row as stored (a copy), or
 * `undefined` when there is none, to the row to store, or to `undefined` to
 * write nothing. It must be pure and synchronous: it may not await, call the
 * store or read anything else. If it throws, nothing is written and the error
 * reaches the caller unchanged.
 */
export type RecordStep = (stored: LeafRecord | undefined) => LeafRecord | undefined;

/**
 * What `transitionRecord` did: `record` is the row as stored afterwards (or
 * `undefined`), and `written` is true only when this call's own step result
 * was stored. A row this call wrote and a row another writer had already
 * resolved can look the same, so a caller that acts on its own write keys on
 * `written`, never on the returned status.
 */
export interface TransitionResult {
  record: LeafRecord | undefined;
  written: boolean;
}

/** Fields fixed at reservation, compared by value; `createdAt` is not, since two devices' copies of one reservation may differ in it. */
const FIXED_AT_RESERVATION = ['vaultId', 'treeIndex', 'leaf', 'purpose', 'nextKey', 'validUntilSlot', 'op'] as const;

/** Value equality over the plain JSON-like data a record holds; a property set to `undefined` equals an absent one, and key order is ignored. */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined || a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const bb = b as unknown[];
    return a.length === bb.length && a.every((x, i) => sameValue(x, bb[i]));
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(ao), ...Object.keys(bo)]);
  for (const k of keys) if (!sameValue(ao[k], bo[k])) return false;
  return true;
}

/** The store's terminal-status check (docs/17 L8), shared by `putRecord`, `update` and `transitionRecord`: no write gives a `landed` or `burned` row an unresolved status. Throws `LedgerConflict`. */
export function checkNotReopened(stored: LeafRecord | undefined, next: LeafRecord): void {
  if (stored && TERMINAL.includes(stored.status) && UNRESOLVED.includes(next.status)) {
    throw new LedgerConflict(`record ${stored.key} is ${stored.status}; no write returns it to ${next.status}`);
  }
}

/**
 * The store-level checks of a record write (docs/17 L8), one function so the
 * three stores apply the same ones: the result must name `key`; over an
 * existing row it may not change a field fixed at reservation (another
 * digest keeps the message `update` has always used, R14), may not change or
 * remove a stored `signature` or `saltTrial` (deterministic for its leaf and
 * digest), and may not give a resolved row an unresolved status. Throws
 * `LedgerConflict`, before anything is written.
 */
export function checkRecordWrite(key: string, stored: LeafRecord | undefined, next: LeafRecord): void {
  if (next.key !== key) throw new LedgerConflict(`a write to record ${key} names another key, ${next.key}`);
  if (!stored) return;
  if (stored.digest !== next.digest) throw new LedgerConflict(`record ${key} is absent or carries another digest`);
  for (const f of FIXED_AT_RESERVATION) {
    if (!sameValue(stored[f], next[f])) throw new LedgerConflict(`record ${key}: ${f} is fixed at reservation and cannot change`);
  }
  if (stored.signature !== undefined && next.signature !== stored.signature) throw new LedgerConflict(`record ${key}: its stored signature cannot change or be removed`);
  if (stored.saltTrial !== undefined && next.saltTrial !== stored.saltTrial) throw new LedgerConflict(`record ${key}: its stored salt trial cannot change or be removed`);
  checkNotReopened(stored, next);
}

/**
 * Run a `RecordStep` on a copy of `stored` and check its result: what every
 * store's `transitionRecord` does between its read and its write. Returns the
 * row to write, or `undefined` for "write nothing". A thenable result (an
 * asynchronous step) is refused with `LedgerConflict`; an error the step
 * throws propagates unchanged.
 */
export function applyRecordStep(key: string, stored: LeafRecord | undefined, step: RecordStep): LeafRecord | undefined {
  const next: unknown = step(stored === undefined ? undefined : structuredClone(stored));
  if (next === undefined) return undefined;
  if (next === null || typeof next !== 'object' || typeof (next as { then?: unknown }).then === 'function') {
    throw new LedgerConflict(`the step for record ${key} must return a record or undefined, synchronously`);
  }
  checkRecordWrite(key, stored, next as LeafRecord);
  return next as LeafRecord;
}

/**
 * The outcome of `reserveOrdinary`: `ok: true` once the record is durably
 * inserted, or the reason it was refused (R22). `no-marker`/`marker-expired`/
 * `retired` mean the tree is not signable on this device; `unresolved` means
 * some record of the tree (any leaf) is still `reserved`/`signed`/`sent`;
 * `exists` means only this leaf's key collided and another may be tried.
 */
export type ReserveOutcome = { ok: true } | { ok: false; reason: 'exists' | 'unresolved' | 'retired' | 'no-marker' | 'marker-expired' };

export interface LedgerStore {
  getRecord(key: string): Promise<LeafRecord | undefined>;
  /**
   * Raw upsert, for tests. No product code uses it: records are inserted by
   * `reserve`/`reserveOrdinary` and changed by `transitionRecord`
   * (`importPending` included). It still refuses, with `LedgerConflict`, to
   * give a `landed` or `burned` row an unresolved status (docs/17 L8), so no
   * store method reopens a resolved row; every other field it may change.
   */
  putRecord(record: LeafRecord): Promise<void>;
  /** Insert `record` only if no record exists for its key, atomically; `false` if one exists (R14). Used only for the recovery message, which is allowed in every mode. */
  reserve(record: LeafRecord): Promise<boolean>;
  /**
   * Insert `record` only if the vault/tree is eligible for ordinary signing
   * and no record exists for its key, atomically with the eligibility check
   * (R22): the tree's marker must exist and not be expired, no record of the
   * tree may be `burned`, and no record of the tree may be unresolved
   * (`reserved`/`signed`/`sent`). Never used for the recovery message.
   */
  reserveOrdinary(record: LeafRecord, currentSlot: bigint): Promise<ReserveOutcome>;
  /**
   * Replace the record with this key only if it exists with the same digest,
   * atomically; throws `LedgerConflict` otherwise (R14). It applies
   * `transitionRecord`'s checks too (docs/17 L8), so a stale full
   * replacement can neither reopen a resolved row nor change a field fixed
   * at reservation or a stored signature. The signer no longer calls it.
   */
  update(record: LeafRecord): Promise<void>;
  /**
   * Every record write after the reservation (docs/17 L8, R75): read the row
   * by `key`, call `step` on a copy, check its result (`applyRecordStep`) and
   * store it, as one atomic unit, so no other write to the store interleaves
   * between the read and the write. `step` returning `undefined` writes
   * nothing. Refuses with `LedgerConflict`, writing nothing, a result that
   * names another key, changes a field fixed at reservation or a stored
   * `signature`/`saltTrial`, or gives a `landed`/`burned` row an unresolved
   * status. With no row stored, a result is inserted. Resolves only once the
   * write is durable, like every `put`.
   */
  transitionRecord(key: string, step: RecordStep): Promise<TransitionResult>;
  listRecords(vaultId: string, treeIndex: number): Promise<LeafRecord[]>;
  getMarker(vaultId: string, treeIndex: number): Promise<TreeMarker | undefined>;
  putMarker(marker: TreeMarker): Promise<void>;
}

/** In-memory store for tests and for the Node signer; `toJSON`/`fromJSON` model backups and stale copies. */
export class MemoryLedgerStore implements LedgerStore {
  private records = new Map<string, LeafRecord>();
  private markers = new Map<string, TreeMarker>();

  async getRecord(key: string): Promise<LeafRecord | undefined> {
    const r = this.records.get(key);
    return r ? structuredClone(r) : undefined;
  }
  async putRecord(record: LeafRecord): Promise<void> {
    checkNotReopened(this.records.get(record.key), record);
    this.records.set(record.key, structuredClone(record));
  }
  async reserve(record: LeafRecord): Promise<boolean> {
    if (this.records.has(record.key)) return false;
    this.records.set(record.key, structuredClone(record));
    return true;
  }
  async reserveOrdinary(record: LeafRecord, currentSlot: bigint): Promise<ReserveOutcome> {
    const marker = this.markers.get(markerKey(record.vaultId, record.treeIndex));
    if (!marker) return { ok: false, reason: 'no-marker' };
    if (BigInt(marker.validUntilSlot) < currentSlot) return { ok: false, reason: 'marker-expired' };
    const treeRecords = [...this.records.values()].filter((r) => r.vaultId === record.vaultId && r.treeIndex === record.treeIndex);
    if (treeRecords.some((r) => r.status === 'burned')) return { ok: false, reason: 'retired' };
    if (treeRecords.some((r) => UNRESOLVED.includes(r.status))) return { ok: false, reason: 'unresolved' };
    if (this.records.has(record.key)) return { ok: false, reason: 'exists' };
    this.records.set(record.key, structuredClone(record));
    return { ok: true };
  }
  async update(record: LeafRecord): Promise<void> {
    const existing = this.records.get(record.key);
    if (!existing) throw new LedgerConflict(`record ${record.key} is absent or carries another digest`);
    checkRecordWrite(record.key, existing, record);
    this.records.set(record.key, structuredClone(record));
  }
  /** The read, the step, the checks and the write run in one synchronous stretch, with no `await` between them, so no other call on this store interleaves (as for `reserve` and `update`). */
  async transitionRecord(key: string, step: RecordStep): Promise<TransitionResult> {
    const stored = this.records.get(key);
    const next = applyRecordStep(key, stored, step);
    if (next === undefined) return { record: stored ? structuredClone(stored) : undefined, written: false };
    const copy = structuredClone(next);
    this.records.set(key, copy);
    return { record: structuredClone(copy), written: true };
  }
  async listRecords(vaultId: string, treeIndex: number): Promise<LeafRecord[]> {
    return [...this.records.values()].filter((r) => r.vaultId === vaultId && r.treeIndex === treeIndex).map((r) => structuredClone(r));
  }
  async getMarker(vaultId: string, treeIndex: number): Promise<TreeMarker | undefined> {
    const m = this.markers.get(markerKey(vaultId, treeIndex));
    return m ? structuredClone(m) : undefined;
  }
  async putMarker(marker: TreeMarker): Promise<void> {
    this.markers.set(marker.key, structuredClone(marker));
  }

  toJSON(): { records: LeafRecord[]; markers: TreeMarker[] } {
    return { records: [...this.records.values()], markers: [...this.markers.values()] };
  }
  static fromJSON(json: { records: LeafRecord[]; markers: TreeMarker[] }): MemoryLedgerStore {
    const s = new MemoryLedgerStore();
    for (const r of json.records) s.records.set(r.key, structuredClone(r));
    for (const m of json.markers) s.markers.set(m.key, structuredClone(m));
    return s;
  }
}

/**
 * The unresolved records of a tree (`pending.json` export for a device
 * move). Never includes markers: the receiving device must earn its own
 * eligibility through `VaultSigner.open`/`sync` against the chain, not
 * inherit one from the exporting device (R20). It can also earn one through
 * `VaultSigner.resolveEarlier` (R69, docs/17 option A, case h): an exported
 * `sent` record carrying `nextKey` keeps its `txSignature`, and if that
 * transaction is proven to have landed the rotation, a ledger that imported
 * it gains the successor marker exactly as the exporting ledger would, when
 * it reconciles within the marker lifetime counted from the landing, no
 * operation has landed under the new key since, and it holds no marker and
 * no record on the new tree (M1-M6). No proof can exclude a signature the
 * exporting device exposed on the new tree without landing it. So the
 * absence of an exported marker does not by itself guarantee a rotation
 * before ordinary signing (R76).
 */
export async function exportPending(store: LedgerStore, vaultId: string, treeIndex: number): Promise<LeafRecord[]> {
  return (await store.listRecords(vaultId, treeIndex)).filter((r) => UNRESOLVED.includes(r.status));
}

/**
 * Import records from another device's export. An imported record never
 * overrides a local record that already resolved, and never lowers a status.
 * Each record is one `transitionRecord` (docs/17 L8), so the rank check and
 * the write are one atomic step: a new row is inserted whole (`updatedAt`
 * now); an existing row of lower rank takes only the imported `status`,
 * `signature`, `saltTrial`, `txSignature` and `note` (those present) and
 * `updatedAt` now (never earlier than stored), and keeps every field fixed
 * at reservation as stored. Another digest throws, as before; another stored
 * signature or salt trial is refused with `LedgerConflict`, and the import
 * throws. An imported `sent` record is judged by the transaction it names
 * (`txSignature`), like any other (docs/17 2.6, case h).
 */
export async function importPending(store: LedgerStore, records: LeafRecord[]): Promise<number> {
  const rank: Record<LeafStatus, number> = { reserved: 0, signed: 1, sent: 2, landed: 3, burned: 3 };
  let imported = 0;
  for (const r of records) {
    const now = Date.now();
    const { written } = await store.transitionRecord(r.key, (local) => {
      if (!local) return { ...r, updatedAt: now };
      if (local.digest !== r.digest) throw new Error(`ledger conflict on ${r.key}: two digests for one leaf`);
      if (rank[local.status] >= rank[r.status]) return undefined;
      const merged: LeafRecord = { ...local, status: r.status, updatedAt: Math.max(local.updatedAt, now) };
      if (r.signature !== undefined) merged.signature = r.signature;
      if (r.saltTrial !== undefined) merged.saltTrial = r.saltTrial;
      if (r.txSignature !== undefined) merged.txSignature = r.txSignature;
      if (r.note !== undefined) merged.note = r.note;
      return merged;
    });
    if (written) imported++;
  }
  return imported;
}
